import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { fetchConfig, requestDownloadLink, getDownloadsByToken } from "@/lib/api";

export default function DownloadsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [email, setEmail] = useState("");
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  const [tokenState, setTokenState] = useState({ status: token ? "loading" : "idle", downloads: [], email: null });

  useEffect(() => {
    // Ensure we have a session token before making any authenticated calls.
    // fetchConfig must complete first so the session token is available.
    fetchConfig()
      .then(() => {
        setConfigReady(true);
        if (!token) return;
        return getDownloadsByToken(token)
          .then((data) => setTokenState({ status: "ready", downloads: data.downloads || [], email: data.email }))
          .catch(() => setTokenState({ status: "error", downloads: [], email: null }));
      })
      .catch(() => {
        setConfigReady(true);
        if (token) {
          setTokenState({ status: "error", downloads: [], email: null });
        }
      });
  }, [token]);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      // Ensure session token is available before making the API call
      if (!configReady) {
        await fetchConfig();
      }
      const data = await requestDownloadLink(email);
      setRequested(true);
      toast.success(data?.message || "Check your email for a download link.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      <Toaster position="top-center" theme="dark" />
      <div className="container section-padding" style={{ maxWidth: "900px" }}>
        <div className="hero-kicker" style={{ marginBottom: "24px" }}>Your Library</div>

        {/* Token mode */}
        {token && (
          <>
            {tokenState.status === "loading" && (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{ width: "40px", height: "40px", margin: "0 auto", border: "2px solid rgba(201,162,39,0.3)", borderTopColor: "var(--gold-warm)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                <p style={{ marginTop: "24px", color: "var(--text-muted)" }}>Loading your downloads…</p>
              </div>
            )}

            {tokenState.status === "error" && (
              <div style={{ padding: "40px 0" }}>
                <h1 style={{ fontSize: "2.4rem" }}>This link is invalid or has expired.</h1>
                <p style={{ marginTop: "16px", color: "var(--text-secondary)" }}>Request a fresh link with your email below.</p>
                <button onClick={() => navigate("/downloads")} className="btn btn-outline" style={{ marginTop: "24px" }}>
                  Request a new link →
                </button>
              </div>
            )}

            {tokenState.status === "ready" && (
              <>
                <h1 style={{ fontSize: "3rem" }}>Welcome back.</h1>
                <p style={{ marginTop: "24px", fontSize: "1.05rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
                  Downloads for <span style={{ color: "var(--gold-warm)" }}>{tokenState.email}</span>.
                </p>
                <div style={{ marginTop: "40px", borderTop: "1px solid var(--border-glass)" }}>
                  {tokenState.downloads.map((d) => (
                    <a
                      key={d.id}
                      href={d.url || "#"}
                      target={d.url ? "_blank" : undefined}
                      rel={d.url ? "noopener noreferrer" : undefined}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px", padding: "20px 0", borderBottom: "1px solid var(--border-glass)", textDecoration: "none", color: "inherit", opacity: d.url ? 1 : 0.6 }}
                      onClick={(e) => { if (!d.url) e.preventDefault(); }}
                    >
                      <div>
                        <p style={{ fontFamily: "var(--font-serif)", fontSize: "1.4rem", fontWeight: 600 }}>{d.title}</p>
                        <p style={{ fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: "4px" }}>PDF · Instant download</p>
                      </div>
                      <span style={{ color: "var(--gold-warm)", fontWeight: 600 }}>
                        {d.url ? "Download →" : "Sent by email"}
                      </span>
                    </a>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Email request mode */}
        {!token && (
          <>
            <h1 style={{ fontSize: "3rem" }}>Recover your downloads.</h1>
            <p style={{ marginTop: "24px", fontSize: "1.05rem", color: "var(--text-secondary)", lineHeight: 1.8, maxWidth: "600px" }}>
              Enter the email you used at checkout and we'll send you a fresh link to your downloads.
            </p>

            {requested ? (
              <div style={{ marginTop: "40px", border: "1px solid var(--border-gold)", borderRadius: "var(--radius-md)", background: "rgba(201,162,39,0.06)", padding: "24px", maxWidth: "500px" }}>
                <p style={{ fontSize: "1rem", color: "var(--text-primary)", lineHeight: 1.7 }}>
                  We've sent an email to <span style={{ color: "var(--gold-warm)" }}>{email}</span>.
                </p>
                <p style={{ marginTop: "12px", fontSize: "0.925rem", color: "var(--text-secondary)", lineHeight: 1.7 }}>
                  If you have a purchase on file, the email contains your download link. Check your inbox (and spam folder).
                </p>
              </div>
            ) : (
              <form onSubmit={handleRequest} noValidate style={{ marginTop: "40px", maxWidth: "500px" }}>
                <label htmlFor="downloads-email" className="sr-only">Email address</label>
                <input
                  id="downloads-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  aria-label="Email address"
                  style={{ width: "100%", padding: "12px 16px", borderRadius: "4px", background: "rgba(7,17,31,0.8)", border: "1px solid var(--border-glass)", color: "#FFF", fontSize: "1rem", marginBottom: "12px" }}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary-gold btn-lg"
                  style={{ width: "100%", opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? "Sending…" : "Email me my downloads"}
                </button>
              </form>
            )}
          </>
        )}

        <div style={{ marginTop: "48px" }}>
          <button onClick={() => navigate("/")} className="btn btn-outline">
            ← Back to home
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
